import type {
  NarrativeOcgFinding,
  NarrativeOcgSeverity,
  NarrativeOcgValidationHint,
} from '../types'

export type OcgFieldName = 'duration' | 'activity_code' | 'task_code'

export type OcgFieldViolation = {
  field: OcgFieldName
  severity: NarrativeOcgSeverity
  message?: string
  suggestedValue?: unknown
  finding: NarrativeOcgFinding
}

const severityRank: Record<NarrativeOcgSeverity, number> = {
  error: 3,
  warning: 2,
  info: 1,
}

const categoryToField = (
  finding: NarrativeOcgFinding
): OcgFieldName | undefined => {
  const category = finding.violation_category?.toLowerCase()

  if (category === 'time') return 'duration'
  if (category === 'activity') return 'activity_code'
  if (category === 'coding') return 'task_code'
  return undefined
}

const isOcgFieldName = (value: unknown): value is OcgFieldName =>
  value === 'duration' || value === 'activity_code' || value === 'task_code'

const getFindingFieldNames = (
  finding: NarrativeOcgFinding
): OcgFieldName[] => {
  const categoryField = categoryToField(finding)
  if (categoryField) return [categoryField]

  const fields = new Set<OcgFieldName>()
  if (isOcgFieldName(finding.field)) {
    fields.add(finding.field)
  }
  if (Array.isArray(finding.affected_fields)) {
    finding.affected_fields.forEach(field => {
      if (isOcgFieldName(field)) fields.add(field)
    })
  }
  return Array.from(fields)
}

export const getOcgFieldViolations = (
  hint?: NarrativeOcgValidationHint
): OcgFieldViolation[] =>
  hint?.visible
    ? (hint.findings ?? []).flatMap(finding => {
        const fields = getFindingFieldNames(finding)
        if (!fields.length) return []
        return fields.map(field => ({
          field,
          severity: finding.severity ?? 'warning',
          message: finding.message,
          suggestedValue: finding.suggested_value,
          finding,
        }))
      })
    : []

export const getOcgFieldSeverity = (
  hint: NarrativeOcgValidationHint | undefined,
  field: OcgFieldName
): NarrativeOcgSeverity | undefined => {
  const violations = getOcgFieldViolations(hint).filter(
    violation => violation.field === field
  )
  if (violations.some(violation => violation.severity === 'error'))
    return 'error'
  if (violations.some(violation => violation.severity === 'warning'))
    return 'warning'
  return violations[0]?.severity
}

export const getOcgFieldSuggestion = (
  hint: NarrativeOcgValidationHint | undefined,
  field: OcgFieldName
): OcgFieldViolation | undefined =>
  getOcgFieldViolations(hint)
    .filter(
      violation =>
        violation.field === field &&
        violation.suggestedValue !== null &&
        violation.suggestedValue !== undefined &&
        violation.suggestedValue !== ''
    )
    .sort((a, b) => severityRank[b.severity] - severityRank[a.severity])[0]

export const hasOcgFieldSuggestions = (
  hint?: NarrativeOcgValidationHint
): boolean =>
  getOcgFieldViolations(hint).some(
    violation =>
      violation.field === 'duration' &&
      violation.severity === 'error' &&
      violation.suggestedValue !== null &&
      violation.suggestedValue !== undefined &&
      violation.suggestedValue !== ''
  )
